import React, { useState, FormEvent } from 'react';
import styled from 'styled-components';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import { ArrowRight, XCircleFill, Eye, EyeSlash } from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';

export const LoginPage: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [isError, setIsError] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const validateEmail = (email: string): boolean => {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setIsError(!validateEmail(e.target.value));
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    // Perform login logic here
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <AuthWrapper>
      <Container>
        <AuthCard>
          <AuthTitle>Log In</AuthTitle>
          <Form onSubmit={handleSubmit}>
            <Form.Group controlId="formBasicEmail" className="mb-3">
              <Form.Control
                type="email"
                placeholder="Email"
                value={email}
                onChange={handleEmailChange}
                isInvalid={isError}
              />
              <Form.Control.Feedback type="invalid" className="text-danger">
                Please enter a valid email address.
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group controlId="formBasicPassword" className="position-relative">
              <Form.Control
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mb-3"
              />
              <PasswordToggleIcon onClick={toggleShowPassword}>
                {showPassword ? <EyeSlash /> : <Eye />}
              </PasswordToggleIcon>
            </Form.Group>
            <div className="d-flex justify-content-between align-items-center">
              <LoginButton variant="primary" type="submit" className="w-50">
                Log In <ArrowRight />
              </LoginButton>
              <ForgotPasswordLink to="/forgot-password">
                Forgot password?
              </ForgotPasswordLink>
            </div>
            {isError && (
                <ErrorAlert>
                    <ErrorIcon />
                    Invalid email or password.
                </ErrorAlert>
            )}
            <Divider>
              <DividerLine />
              <DividerText>Or</DividerText>
              <DividerLine />
            </Divider>
            <div className="d-flex justify-content-center">
                <GoogleLoginButton>
                    <GoogleLogoCircle>
                    <img src="https://upload.wikimedia.org/wikipedia/commons/5/53/Google_%22G%22_Logo.svg" alt="Google Logo" width="20" />
                    </GoogleLogoCircle>
                    Log In with Google
                </GoogleLoginButton>
            </div>
          </Form>
          <BottomText>
            Don't have an account? <Link to="/signup">Sign Up</Link>
          </BottomText>
        </AuthCard>
      </Container>
    </AuthWrapper>
  );
};

// Add the styled components for LoginPage here
const AuthWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
`;

const AuthCard = styled.div`
  background-color: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 40px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
`;

const AuthTitle = styled.h2`
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 30px;
  text-align: center;
`;

const ErrorAlert = styled.div`
  display: flex;
  align-items: center;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  padding: 10px;
  margin: 10px 0;
  color: #721c24;
`;

const ErrorIcon = styled(XCircleFill)`
  margin-right: 8px;
`;

const GoogleLoginButton = styled(Button)`
  font-weight: bold;  
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #007bff;
  color: white;
  margin-top: 20px;
`;

const GoogleLogoCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: white;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  margin-right: 8px;
`;

const LoginButton = styled(Button)`
  font-weight: bold;
  background-color: #28a745;
  border-color: #28a745;
  color: white;

  &:hover,
  &:active,
  &:focus {
    background-color: #218838;
    border-color: #218838;
    color: white;
  }

  svg {
    font-weight: bold;
  }
`;

const ForgotPasswordLink = styled(Link)`
  text-decoration: none;
  color: #6c757d;
  font-size: 0.9rem;
`;

const BottomText = styled.p`
  margin-top: 40px;
  font-size: 0.9rem;

  a {
    font-weight: bold;
    color: #007bff;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const PasswordToggleIcon = styled.span`
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  cursor: pointer;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 20px 0;
`;

const DividerText = styled.span`
  background-color: white;
  padding: 0 10px;
  font-size: 0.9rem;
  color: #6c757d;
`;

const DividerLine = styled.div`
  background-color: #ccc;
  height: 1px;
  flex-grow: 1;
`;

